import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguageStore } from '../../../store/languageStore';
import { ROOM_POLLING_INTERVAL_MS } from '../../../utils/polling';
import { getUITranslation, getMotivatorTranslation } from '../i18n/translations';
import { useMovingMotivatorsStore } from '../store/movingMotivatorsStore';
import { MotivatorList } from '../components';
import { Motivator } from '../types';
import { getRoom, updateCardPositions, getParticipantRankings, ParticipantRanking } from '../services/api';

const motivatorIds = [
  'curiosity',
  'honor',
  'acceptance',
  'mastery',
  'power',
  'freedom',
  'relatedness',
  'order',
  'goal',
  'status',
];

export const Play = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const { language } = useLanguageStore();
  const t = getUITranslation(language);

  const { ranking, setRanking, initializeMotivators, roomInfo, participants, setRoomInfo, setParticipants } =
    useMovingMotivatorsStore();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create motivators with current language
  const motivators: Motivator[] = useMemo(
    () =>
      motivatorIds.map((id) => {
        const translation = getMotivatorTranslation(language, id);
        return {
          id,
          name: translation.name,
          description: translation.description,
          image: `/apps/moving-motivators/${id}.png`,
        };
      }),
    [language]
  );

  // Load room data from API
  useEffect(() => {
    const loadRoom = async () => {
      if (!roomId) return;

      try {
        setLoading(true);
        setError(null);

        const room = await getRoom(roomId);

        // Set room info
        setRoomInfo({
          id: room.id,
          roomName: room.roomName,
          visibility: room.visibility,
          createdBy: room.createdBy.email,
          currentUserEmail: sessionStorage.getItem('userEmail') || 'user@example.com', // Get from sessionStorage
        });

        // Get participant rankings
        const participantRankings: ParticipantRanking[] = getParticipantRankings(
          room,
          motivatorIds
        );
        setParticipants(participantRankings);

        setLoading(false);
      } catch (err) {
        console.error('Failed to load room:', err);
        setError(err instanceof Error ? err.message : 'Failed to load room');
        setLoading(false);
      }
    };

    loadRoom();
  }, [roomId, setRoomInfo, setParticipants]);

  // Poll for participants updates
  useEffect(() => {
    if (!roomId) return;

    const pollInterval = setInterval(async () => {
      try {
        console.log('[5s Poll] Fetching room data for participants update...');
        const room = await getRoom(roomId);
        console.log('[5s Poll] Room data:', {
          players: room.players.length,
          cards: room.cards.length,
        });
        
        const participantRankings: ParticipantRanking[] = getParticipantRankings(
          room,
          motivatorIds
        );
        console.log('[5s Poll] Updated participant rankings:', participantRankings);
        setParticipants(participantRankings);
      } catch (err) {
        console.error('Failed to update participants:', err);
      }
    }, ROOM_POLLING_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [roomId, motivatorIds, setParticipants]);

  useEffect(() => {
    initializeMotivators(motivators);
  }, [motivators, initializeMotivators]);

  // Auto-save ranking to backend on change
  useEffect(() => {
    if (!roomId) return;

    // Debounce: only save after 1 second of no changes
    const timer = setTimeout(async () => {
      try {
        const userEmail = sessionStorage.getItem('userEmail') || 'user@example.com';
        await updateCardPositions(roomId, {
          playerId: userEmail,
          cards: ranking.map((cardId, index) => ({
            cardId,
            position: index + 1,
          })),
        });
        console.log('Ranking saved successfully');
      } catch (err) {
        console.error('Failed to save ranking:', err);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [ranking, roomId]);

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary mx-auto" />
            <p className="text-muted-foreground">Loading room...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="rounded-md bg-destructive/10 p-6 text-center">
          <p className="text-destructive font-medium">{error}</p>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Header */}
          <div className="mb-8">
            <h1 className="mb-2 text-4xl font-bold">Moving Motivators</h1>
            {roomId && (
              <p className="mt-2 text-sm text-muted-foreground">Room ID: {roomId}</p>
            )}
          </div>

          {/* Instructions - Desktop (Horizontal) */}
          <div className="mb-6 hidden px-4 md:block">
            <div className="relative flex items-center justify-between">
              {/* Left Arrow and Text */}
              <div className="flex items-center gap-2">
                <div className="text-2xl">←</div>
                <span className="text-sm font-semibold">{t.play.mostImportant}</span>
              </div>
              
              {/* Right Arrow and Text */}
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{t.play.leastImportant}</span>
                <div className="text-2xl">→</div>
              </div>
            </div>
          </div>

          {/* Instructions - Mobile (Vertical) - Top */}
          <div className="mb-4 flex justify-center md:hidden">
            <div className="flex flex-col items-center gap-1">
              <div className="text-2xl">↑</div>
              <span className="text-sm font-semibold">{t.play.mostImportant}</span>
            </div>
          </div>

          {/* Motivator Cards */}
          <div className="mb-8">
            <MotivatorList
              motivators={motivators}
              ranking={ranking}
              onRankingChange={setRanking}
            />
          </div>

          {/* Instructions - Mobile (Vertical) - Bottom */}
          <div className="mb-8 flex justify-center md:hidden">
            <div className="flex flex-col items-center gap-1">
              <span className="text-sm font-semibold">{t.play.leastImportant}</span>
              <div className="text-2xl">↓</div>
            </div>
          </div>

          {/* All Participants Rankings */}
          {roomInfo && (roomInfo.currentUserEmail === roomInfo.createdBy || roomInfo.visibility === 'public') && participants.length > 0 && (
            <div className="mt-12 rounded-lg border border-border bg-card p-6">
              <h3 className="mb-6 text-center text-xl font-bold">All Participants Rankings</h3>
              
              {/* Desktop - Horizontal Table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-3 pr-4 text-left text-sm font-semibold">Participant</th>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((pos) => (
                        <th key={pos} className="pb-3 px-2 text-center text-xs font-semibold">
                          {pos}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {participants.map((participant, idx) => (
                      <tr key={idx} className="border-b border-border last:border-0">
                        <td className="py-3 pr-4 text-sm font-medium">
                          {participant.playerName}
                          {participant.email === roomInfo.currentUserEmail && (
                            <span className="ml-2 text-xs text-primary">(You)</span>
                          )}
                        </td>
                        {participant.ranking.map((motivatorId, position) => {
                          const motivator = motivators.find((m) => m.id === motivatorId);
                          return (
                            <td key={position} className="px-2 py-3 text-center">
                              <div className="text-xs">{motivator?.name || motivatorId}</div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile - Vertical Cards */}
              <div className="space-y-6 md:hidden">
                {participants.map((participant, idx) => (
                  <div key={idx} className="rounded-lg border border-border bg-muted/30 p-4">
                    <h4 className="mb-3 font-semibold">
                      {participant.playerName}
                      {participant.email === roomInfo.currentUserEmail && (
                        <span className="ml-2 text-xs text-primary">(You)</span>
                      )}
                    </h4>
                    <div className="space-y-2">
                      {participant.ranking.map((motivatorId, position) => {
                        const motivator = motivators.find((m) => m.id === motivatorId);
                        return (
                          <div key={position} className="flex items-center gap-3 rounded-md bg-background px-3 py-2">
                            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                              {position + 1}
                            </span>
                            <span className="text-sm">{motivator?.name || motivatorId}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
